import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsApi, menuApi, type EventMenuSelection } from '../services/api';
import { formatShiftTime } from '../lib/shiftTime';
import { toast } from '../stores/toastStore';
import {
  ArrowLeft,
  Users,
  MapPin,
  CalendarPlus,
  AlertTriangle,
  Plus,
  X,
  Send,
  Link2,
  Check,
  ChefHat,
  FileText,
  CircleDollarSign,
  ClipboardList,
  Copy,
} from 'lucide-react';
import clsx from 'clsx';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const STATUS_META: Record<string, { label: string; className: string }> = {
  inquiry: { label: 'Inquiry', className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  proposal_sent: { label: 'Proposal out', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  confirmed: { label: 'Confirmed', className: 'bg-success-50 text-success-700 border border-success-200' },
  completed: { label: 'Completed', className: 'bg-slate-100 text-slate-600 border border-slate-200' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-600 border border-red-200' },
};

const STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'proposal_sent', label: 'Proposal out' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface FormState {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  spaceName: string;
  expectedGuests: number;
  menuSelections: EventMenuSelection[];
  roomFee: string;     // dollars, as typed
  servicePct: string;
  taxPct: string;
  deposit: string;     // dollars, as typed
  briefingNotes: string;
}

export default function EventDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event-detail', id],
    queryFn: async () => {
      const response = await eventsApi.getById(id);
      return response.data?.data;
    },
    enabled: !!id,
  });

  const { data: menuItems = [] } = useQuery({
    queryKey: ['event-menu-items'],
    queryFn: async () => {
      const response = await menuApi.getItems();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as Array<{ id: string; name: string; priceCents: number }>;
    },
  });

  // Initialize the editable form from the loaded event (once per load)
  useEffect(() => {
    if (!event || form) return;
    const fin = event.eventFinancials || {};
    setForm({
      clientName: event.clientName || '',
      clientEmail: event.clientEmail || '',
      clientPhone: event.clientPhone || '',
      clientCompany: event.clientCompany || '',
      spaceName: event.spaceName || '',
      expectedGuests: event.expectedGuests || 0,
      menuSelections: Array.isArray(event.menuSelections) ? event.menuSelections : [],
      roomFee: fin.roomFeeCents ? String(fin.roomFeeCents / 100) : '',
      servicePct: fin.servicePct ? String(fin.servicePct) : '',
      taxPct: fin.taxPct ? String(fin.taxPct) : '',
      deposit: fin.depositCents ? String(fin.depositCents / 100) : '',
      briefingNotes: event.briefingNotes || '',
    });
  }, [event, form]);

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['event-detail', id] });
    queryClient.invalidateQueries({ queryKey: ['events-pipeline'] });
    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  const buildPayload = () => {
    if (!form) return {};
    const dollars = (v: string) => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
    };
    const pct = (v: string) => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return {
      clientName: form.clientName,
      clientEmail: form.clientEmail,
      clientPhone: form.clientPhone,
      clientCompany: form.clientCompany,
      spaceName: form.spaceName,
      expectedGuests: form.expectedGuests,
      menuSelections: form.menuSelections,
      eventFinancials: {
        roomFeeCents: dollars(form.roomFee),
        servicePct: pct(form.servicePct),
        taxPct: pct(form.taxPct),
        depositCents: dollars(form.deposit),
      },
      briefingNotes: form.briefingNotes,
    };
  };

  const saveMutation = useMutation({
    mutationFn: () => eventsApi.updateDetails(id, buildPayload()),
    onSuccess: () => {
      toast.success('Event saved.');
      setDirty(false);
      setForm(null); // re-init from fresh server data (recomputed totals/conflicts)
      refetchAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not save just now — please try again.');
    },
  });

  const statusMutation = useMutation({
    mutationFn: (eventStatus: any) => eventsApi.updateDetails(id, { eventStatus }),
    onSuccess: () => { setForm(null); refetchAll(); },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not update the status.');
    },
  });

  const sendProposalMutation = useMutation({
    mutationFn: () => eventsApi.sendProposal(id),
    onSuccess: (res: any) => {
      toast.success(`Proposal sent to ${res.data?.data?.to || 'the client'}.`);
      setForm(null);
      refetchAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not send the proposal just now.');
    },
  });

  const depositLinkMutation = useMutation({
    mutationFn: () => eventsApi.createDepositLink(id),
    onSuccess: (res: any) => {
      const url = res.data?.data?.url;
      if (url && navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => undefined);
        toast.success('Deposit link created and copied to your clipboard.');
      } else {
        toast.success('Deposit link created.');
      }
      setForm(null);
      refetchAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not create a deposit link just now.');
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: () => eventsApi.markDepositPaid(id),
    onSuccess: () => {
      toast.success('Deposit recorded — the event is confirmed.');
      setForm(null);
      refetchAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not record the deposit.');
    },
  });

  const planKitchenMutation = useMutation({
    mutationFn: () => eventsApi.planKitchen(id),
    onSuccess: (res: any) => {
      const n = res.data?.data?.lines?.length || 0;
      toast.success(`Kitchen planned — ${n} menu line${n === 1 ? '' : 's'} pushed to prep & ordering.`);
      setForm(null);
      refetchAll();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not plan the kitchen just now.');
    },
  });

  // Live money preview, computed from the form the same way the server does
  const preview = useMemo(() => {
    if (!form) return null;
    const guests = form.expectedGuests || 0;
    const lines = form.menuSelections.map((s) => {
      const totalQty = s.qtyMode === 'per_guest' ? (s.qty || 0) * guests : (s.qty || 0);
      return { ...s, totalQty, totalCents: Math.round(totalQty * (s.unitPriceCents || 0)) };
    });
    const menuSubtotalCents = lines.reduce((sum, l) => sum + l.totalCents, 0);
    const roomFeeCents = Math.round((parseFloat(form.roomFee) || 0) * 100);
    const servicePct = parseFloat(form.servicePct) || 0;
    const taxPct = parseFloat(form.taxPct) || 0;
    const serviceCents = Math.round(((menuSubtotalCents + roomFeeCents) * servicePct) / 100);
    const taxCents = Math.round(((menuSubtotalCents + roomFeeCents + serviceCents) * taxPct) / 100);
    const totalCents = menuSubtotalCents + roomFeeCents + serviceCents + taxCents;
    const depositCents = Math.round((parseFloat(form.deposit) || 0) * 100);
    return { lines, menuSubtotalCents, roomFeeCents, serviceCents, taxCents, totalCents, depositCents, servicePct, taxPct };
  }, [form]);

  if (isLoading || !event || !form || !preview) {
    return <div className="p-6 text-center text-slate-500">Loading event…</div>;
  }

  const status = event.eventStatus || 'confirmed';
  const meta = STATUS_META[status] || STATUS_META.confirmed;
  const spaces: Array<{ name: string; capacity: number }> = event.eventSettings?.spaces || [];
  const conflicts = event.spaceConflicts || [];
  const estimates = event.estimates;
  const summary = event.staffingSummary || [];

  const formatDate = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

  const addMenuItem = (itemId: string) => {
    const item = menuItems.find((m) => m.id === itemId);
    if (!item) return;
    update({
      menuSelections: [
        ...form.menuSelections,
        { menuItemId: item.id, name: item.name, unitPriceCents: item.priceCents, qtyMode: 'per_guest', qty: 1 },
      ],
    });
  };

  const updateSelection = (idx: number, patch: Partial<EventMenuSelection>) => {
    update({
      menuSelections: form.menuSelections.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    });
  };

  const removeSelection = (idx: number) => {
    update({ menuSelections: form.menuSelections.filter((_, i) => i !== idx) });
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <button
            onClick={() => navigate('/events')}
            className="p-2 hover:bg-slate-100 rounded-lg mt-0.5 flex-shrink-0"
            title="Back to events"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 truncate">{event.name}</h1>
              <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full', meta.className)}>{meta.label}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <CalendarPlus className="w-4 h-4" />
                {formatDate(event.date)}, {formatShiftTime(event.startTime)} – {formatShiftTime(event.endTime)}
              </span>
              {form.spaceName && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {form.spaceName}
                </span>
              )}
              {!!form.expectedGuests && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {form.expectedGuests} guests
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            className="input w-auto"
            value={status}
            onChange={(e) => statusMutation.mutate(e.target.value)}
            disabled={statusMutation.isPending}
            title="Pipeline status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {dirty && (
            <button
              onClick={() => saveMutation.mutate()}
              className="btn btn-primary"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      {/* Conflict warning */}
      {conflicts.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {form.spaceName || 'This space'} is also booked for{' '}
            {conflicts.map((c: any) => `"${c.name}" (${formatShiftTime(c.startTime)} – ${formatShiftTime(c.endTime)})`).join(', ')}{' '}
            — double-check before confirming.
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="card p-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => sendProposalMutation.mutate()}
          className="btn btn-primary inline-flex items-center gap-2"
          disabled={sendProposalMutation.isPending || !event.clientEmail}
          title={event.clientEmail ? `Email the proposal to ${event.clientEmail}` : "Add the client's email first"}
        >
          <Send className="w-4 h-4" />
          {sendProposalMutation.isPending ? 'Sending…' : event.proposalSentAt ? 'Re-send proposal' : 'Send proposal'}
        </button>
        {event.depositPaidAt ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success-50 text-success-700 text-sm">
            <Check className="w-4 h-4" /> Deposit paid
          </span>
        ) : (
          <>
            <button
              onClick={() => depositLinkMutation.mutate()}
              className="btn btn-secondary inline-flex items-center gap-2"
              disabled={depositLinkMutation.isPending || preview.depositCents <= 0}
              title={preview.depositCents > 0 ? 'Create a Stripe payment link for the deposit' : 'Set a deposit amount first'}
            >
              <Link2 className="w-4 h-4" />
              {depositLinkMutation.isPending ? 'Creating…' : event.depositLink ? 'Refresh deposit link' : 'Deposit link'}
            </button>
            {event.depositLink && (
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(event.depositLink).catch(() => undefined);
                  toast.success('Deposit link copied.');
                }}
                className="btn btn-secondary inline-flex items-center gap-2"
                title={event.depositLink}
              >
                <Copy className="w-4 h-4" />
                Copy link
              </button>
            )}
            <button
              onClick={() => markPaidMutation.mutate()}
              className="btn btn-secondary inline-flex items-center gap-2"
              disabled={markPaidMutation.isPending}
              title="Record a deposit received outside Stripe (cash, check, transfer)"
            >
              <CircleDollarSign className="w-4 h-4" />
              Mark deposit paid
            </button>
          </>
        )}
        <button
          onClick={() => planKitchenMutation.mutate()}
          className="btn btn-secondary inline-flex items-center gap-2"
          disabled={planKitchenMutation.isPending}
          title="Push the menu × guests into prep lists and ordering"
        >
          <ChefHat className="w-4 h-4" />
          {planKitchenMutation.isPending ? 'Planning…' : event.prepPushedAt ? 'Refresh prep plan' : 'Plan the kitchen'}
        </button>
        <span className="flex-1" />
        <button
          onClick={() => navigate(`/events/${id}/beo`)}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <ClipboardList className="w-4 h-4" />
          BEO
        </button>
        <button
          onClick={() => navigate(`/events/${id}/beo?view=client`)}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <FileText className="w-4 h-4" />
          Client proposal
        </button>
      </div>

      {/* Wiring status */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full', event.prepPushedAt ? 'bg-success-50 text-success-700' : 'bg-slate-100 text-slate-500')}>
          <ChefHat className="w-3 h-3" />
          {event.prepPushedAt ? 'Prep plan ✓ — see Ordering & prep lists' : 'Kitchen not planned yet'}
        </span>
        <button
          onClick={() => navigate('/events')}
          className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full', (event.totalRequired || 0) > 0 && event.isFilled ? 'bg-success-50 text-success-700' : 'bg-amber-50 text-amber-700')}
          title="Manage staffing on the Events page"
        >
          <Users className="w-3 h-3" />
          {(event.totalRequired || 0) === 0
            ? 'No staffing plan yet — add roles in the Staffing tab'
            : `Staffing ${event.totalAssigned} of ${event.totalRequired} filled`}
        </button>
        {event.proposalSentAt && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500">
            <Send className="w-3 h-3" />
            Proposal sent {new Date(event.proposalSentAt).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Client */}
        <div className="card p-4 md:p-5 space-y-3">
          <h2 className="font-semibold text-slate-900">Client</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
              <input className="input w-full" value={form.clientName} onChange={(e) => update({ clientName: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
              <input className="input w-full" value={form.clientCompany} onChange={(e) => update({ clientCompany: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
              <input type="email" className="input w-full" value={form.clientEmail} onChange={(e) => update({ clientEmail: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
              <input type="tel" className="input w-full" value={form.clientPhone} onChange={(e) => update({ clientPhone: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Space & guests */}
        <div className="card p-4 md:p-5 space-y-3">
          <h2 className="font-semibold text-slate-900">Space &amp; guests</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Space</label>
              {spaces.length > 0 ? (
                <select className="input w-full" value={form.spaceName} onChange={(e) => update({ spaceName: e.target.value })}>
                  <option value="">No space assigned</option>
                  {spaces.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}{s.capacity ? ` (seats ${s.capacity})` : ''}
                    </option>
                  ))}
                  {form.spaceName && !spaces.some((s) => s.name === form.spaceName) && (
                    <option value={form.spaceName}>{form.spaceName}</option>
                  )}
                </select>
              ) : (
                <input
                  className="input w-full"
                  placeholder="e.g., Back Room — or add spaces via Spaces & terms"
                  value={form.spaceName}
                  onChange={(e) => update({ spaceName: e.target.value })}
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Expected guests</label>
              <input
                type="number"
                min={0}
                className="input w-full"
                value={form.expectedGuests}
                onChange={(e) => update({ expectedGuests: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          </div>
          {(() => {
            const cap = spaces.find((s) => s.name === form.spaceName)?.capacity || 0;
            if (cap > 0 && form.expectedGuests > cap) {
              return (
                <p className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {form.expectedGuests} guests is over {form.spaceName}&apos;s seating of {cap}.
                </p>
              );
            }
            return null;
          })()}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Timeline / briefing notes</label>
            <textarea
              className="input w-full"
              rows={3}
              placeholder="6 PM guests arrive · 6:30 passed apps · 7:15 seated dinner · 9 PM toasts…"
              value={form.briefingNotes}
              onChange={(e) => update({ briefingNotes: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="card p-4 md:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Menu</h2>
          <select
            className="input w-auto max-w-[260px]"
            value=""
            onChange={(e) => { if (e.target.value) addMenuItem(e.target.value); }}
          >
            <option value="">Add a menu item…</option>
            {menuItems.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {money(m.priceCents)}
              </option>
            ))}
          </select>
        </div>
        {form.menuSelections.length === 0 ? (
          <p className="text-sm text-slate-400">No menu yet. Pick items from your menu — quantities can be per guest or flat.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-2">Item</th>
                  <th className="py-2 pr-2">Quantity</th>
                  <th className="py-2 pr-2 text-right">Unit price</th>
                  <th className="py-2 pr-2 text-right">Qty total</th>
                  <th className="py-2 pr-2 text-right">Line total</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((line, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-2 pr-2 font-medium text-slate-800">{line.name}</td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={0}
                          step="any"
                          className="input w-16 py-1"
                          value={line.qty}
                          onChange={(e) => updateSelection(idx, { qty: parseFloat(e.target.value) || 0 })}
                        />
                        <select
                          className="input w-auto py-1"
                          value={line.qtyMode}
                          onChange={(e) => updateSelection(idx, { qtyMode: e.target.value as 'per_guest' | 'flat' })}
                        >
                          <option value="per_guest">per guest</option>
                          <option value="flat">flat</option>
                        </select>
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <span className="text-slate-400">$</span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className="input w-20 py-1 text-right"
                          value={(line.unitPriceCents / 100).toString()}
                          onChange={(e) => updateSelection(idx, { unitPriceCents: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                        />
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-right text-slate-600">{line.totalQty}</td>
                    <td className="py-2 pr-2 text-right font-medium">{money(line.totalCents)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => removeSelection(idx)}
                        className="p-1 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Financials */}
        <div className="card p-4 md:p-5 space-y-3">
          <h2 className="font-semibold text-slate-900">Financials</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Room fee ($)</label>
              <input type="number" min={0} step="0.01" className="input w-full" value={form.roomFee} placeholder="0"
                onChange={(e) => update({ roomFee: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Service (%)</label>
              <input type="number" min={0} max={100} step="0.1" className="input w-full" value={form.servicePct} placeholder="0"
                onChange={(e) => update({ servicePct: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tax (%)</label>
              <input type="number" min={0} max={100} step="0.01" className="input w-full" value={form.taxPct} placeholder="0"
                onChange={(e) => update({ taxPct: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Deposit ($)</label>
              <input type="number" min={0} step="0.01" className="input w-full" value={form.deposit} placeholder="0"
                onChange={(e) => update({ deposit: e.target.value })} />
            </div>
          </div>
          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-slate-600"><span>Menu subtotal</span><span>{money(preview.menuSubtotalCents)}</span></div>
            {preview.roomFeeCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Room fee</span><span>{money(preview.roomFeeCents)}</span></div>
            )}
            {preview.serviceCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Service ({preview.servicePct}%)</span><span>{money(preview.serviceCents)}</span></div>
            )}
            {preview.taxCents > 0 && (
              <div className="flex justify-between text-slate-600"><span>Tax ({preview.taxPct}%)</span><span>{money(preview.taxCents)}</span></div>
            )}
            <div className="flex justify-between font-bold text-slate-900 pt-1"><span>Total</span><span>{money(preview.totalCents)}</span></div>
            {preview.depositCents > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Deposit due</span>
                <span className={event.depositPaidAt ? 'text-success-600 font-medium' : ''}>
                  {money(preview.depositCents)}{event.depositPaidAt ? ' · paid' : ''}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* P&L estimates */}
        <div className="card p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Event P&amp;L</h2>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Estimates</span>
          </div>
          {estimates ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Revenue (before tax)</span>
                <span className="font-medium text-slate-900">{money(estimates.revenueExTaxCents)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>
                  Est. food cost
                  {estimates.foodTotalLines > 0 && (
                    <span className="text-xs text-slate-400"> · {estimates.foodCostedLines} of {estimates.foodTotalLines} items costed</span>
                  )}
                </span>
                <span>−{money(estimates.foodCostCents)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>
                  Est. labor ({estimates.laborHours}h scheduled)
                  {estimates.laborMissingRates > 0 && (
                    <span className="text-xs text-slate-400"> · {estimates.laborMissingRates} without a rate</span>
                  )}
                </span>
                <span>−{money(estimates.laborCents)}</span>
              </div>
              <div className="flex justify-between font-bold pt-1 border-t mt-2">
                <span>Est. margin</span>
                <span className={estimates.marginCents >= 0 ? 'text-success-700' : 'text-red-600'}>
                  {money(estimates.marginCents)}
                  {estimates.marginPct !== null && ` (${estimates.marginPct}%)`}
                </span>
              </div>
              <p className="text-xs text-slate-400 pt-2">
                Food cost uses recipe costs where they exist; labor uses scheduled hours × each person&apos;s rate.
                Save changes to refresh.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Estimates appear once the menu and staffing take shape.</p>
          )}
          {summary.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-slate-500 mb-2">Staffing plan</p>
              <div className="flex flex-wrap gap-2">
                {summary.map((row: any) => (
                  <span
                    key={row.role}
                    className={clsx(
                      'text-xs px-2 py-1 rounded-full capitalize',
                      row.isFilled ? 'bg-success-50 text-success-700' : 'bg-amber-50 text-amber-700'
                    )}
                  >
                    {row.role} {row.assigned}/{row.required}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky save on mobile */}
      {dirty && (
        <div className="fixed bottom-4 right-4 md:hidden z-40">
          <button
            onClick={() => saveMutation.mutate()}
            className="btn btn-primary shadow-lg inline-flex items-center gap-2"
            disabled={saveMutation.isPending}
          >
            <Plus className="w-4 h-4 rotate-45 hidden" />
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}
